import { emitAuthEvent } from './auth-events';
import { ApiRequestError, CLIENT_ERROR_CODES, toApiError } from './api-error';
import { env } from './env';

/**
 * The typed fetch client. Every network call in the app goes through here.
 *
 * Contract (ARCHITECTURE §4/§5):
 * - `credentials: 'include'` always — the session is an httpOnly cookie pair
 *   issued by `POST /api/auth/exchange`. The EMS JWT is never stored and never
 *   sent again; that is the deliberate fix for the old WMS's localStorage token.
 * - Every failure is an `ApiRequestError` carrying the server's `{code, params}`
 *   envelope, which the UI renders as `t('errors.<code>', params)`.
 * - A 401 triggers exactly ONE silent `POST /api/auth/refresh`; concurrent 401s
 *   share that single round-trip. If it fails, `session-expired` is emitted once
 *   and the caller still gets the 401 error.
 */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];

export interface RequestOptions {
  method?: HttpMethod;
  /** Serialized as JSON unless it is FormData/Blob/URLSearchParams. */
  body?: unknown;
  /** Appended as a query string; `undefined` values are dropped. */
  query?: Record<string, QueryValue> | URLSearchParams;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Opt out of the silent-refresh retry. Set on the auth endpoints themselves —
   * retrying `/auth/refresh` after a 401 from `/auth/refresh` is an infinite
   * loop with extra steps.
   */
  skipAuthRetry?: boolean;
  /** Return the raw Response instead of parsing (file downloads). */
  raw?: boolean;
}

const REFRESH_PATH = '/auth/refresh';

/**
 * Paths that must never trigger the refresh-and-retry dance: they either ARE
 * the refresh (infinite loop) or run before any session exists.
 *
 * `/auth/me` is deliberately NOT here — it is the one call that most needs the
 * retry. A tab left open past the 15-minute access-token TTL must repair itself
 * from the refresh cookie instead of bouncing the user through EMS.
 */
const NO_RETRY_PATHS = ['/auth/refresh', '/auth/exchange', '/auth/dev-login', '/auth/sso'];

/** Shared across concurrent 401s so a page with 8 queries refreshes once. */
let refreshInFlight: Promise<boolean> | null = null;

export function apiUrl(path: string, query?: RequestOptions['query']): string {
  const base = path.startsWith('http') ? path : `${env.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  const search = query instanceof URLSearchParams ? query : toSearchParams(query);
  const qs = search?.toString();
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
}

export function toSearchParams(query: Record<string, QueryValue> | undefined): URLSearchParams | undefined {
  if (!query) return undefined;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    if (value === null) {
      params.append(key, '');
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) params.append(`${key}[]`, String(item));
      continue;
    }
    params.append(key, String(value as string | number | boolean));
  }
  return params;
}

function buildInit(options: RequestOptions): RequestInit {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  let body: BodyInit | undefined;

  if (options.body !== undefined && options.body !== null) {
    if (
      options.body instanceof FormData ||
      options.body instanceof Blob ||
      options.body instanceof URLSearchParams ||
      typeof options.body === 'string'
    ) {
      // Never set Content-Type for FormData — the boundary must come from fetch.
      body = options.body as BodyInit;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }
  }

  return {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers,
    ...(body === undefined ? {} : { body }),
    ...(options.signal ? { signal: options.signal } : {}),
  };
}

async function rawFetch(path: string, options: RequestOptions): Promise<Response> {
  try {
    return await fetch(apiUrl(path, options.query), buildInit(options));
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiRequestError({
      code: CLIENT_ERROR_CODES.network,
      status: 0,
      isNetworkError: true,
      cause,
    });
  }
}

/**
 * One refresh for all concurrent callers. Returns true when a new session
 * cookie pair was issued.
 */
export async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await rawFetch(REFRESH_PATH, { method: 'POST', body: {}, skipAuthRetry: true });
      return response.ok;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask so callers that awaited this promise all see the
      // same result before a new refresh can start.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return (await response.text()) as T;
  }
  return (await response.json()) as T;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const retryable = !options.skipAuthRetry && !NO_RETRY_PATHS.some((p) => path.startsWith(p));

  let response = await rawFetch(path, options);

  if (response.status === 401 && retryable) {
    const refreshed = await refreshSession();
    if (refreshed) {
      emitAuthEvent({ type: 'session-refreshed' });
      response = await rawFetch(path, options);
    }
    if (response.status === 401) {
      emitAuthEvent({ type: 'session-expired' });
    }
  }

  if (options.raw) {
    if (!response.ok) throw await toApiError(response);
    return response as T;
  }

  if (!response.ok) throw await toApiError(response);
  return parse<T>(response);
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
} as const;

/** Test seam — resets the shared refresh promise between cases. */
export function __resetApiClient(): void {
  refreshInFlight = null;
}
